import React, { useState, useCallback } from 'react'
import { Button, ButtonProps, Typography } from '@mui/material'
import { makeStyles, withStyles } from 'tss-react/mui'
import { FileDrop } from 'react-file-drop'
import { useTranslation, Trans } from 'gatsby-plugin-react-i18next'

import useLeadActions, {
  allowedUploadExtensions,
} from '@talentinc/gatsby-theme-ecom/hooks/useLeadActions'
import SvgUpload from '../../images/upload.inline.svg'
import { useMixpanelCTAClickTracking } from '../../utils/mixpanel'
import { MixpanelCTALinkActions, MixpanelTracking } from '../../types/mixpanel'

export type ResumeUploadButtonCallback =
  | React.DragEventHandler<HTMLDivElement>
  | React.ChangeEventHandler<HTMLInputElement>

export type ResumeUploadButtonEventType =
  | React.DragEvent<HTMLDivElement>
  | React.ChangeEvent<HTMLInputElement>

interface Props {
  onFileSelect: ResumeUploadButtonCallback
  uploadClass?: string
  error: string | Error | null
  ButtonProps?: Partial<ButtonProps<'label'>>
  children: React.ReactNode
  mixpanelTracking?: MixpanelTracking
}

const ResumeUploadButton: React.FC<Props> = ({
  onFileSelect,
  uploadClass,
  error,
  ButtonProps = {},
  children,
  mixpanelTracking,
}) => {
  const { classes, cx } = useStyles()
  const { t } = useTranslation()
  const [draggedFileOver, setDraggedFileOver] = useState(false)
  const dragEnter = useCallback(() => setDraggedFileOver(true), [])
  const dragLeave = useCallback(() => setDraggedFileOver(false), [])
  const handleMpCTAClickTracking = useMixpanelCTAClickTracking()

  return (
    <FileDrop
      onDrop={(_, event) =>
        onFileSelect(
          // @ts-ignore
          event
        )
      }
      onFrameDragEnter={dragEnter}
      onFrameDragLeave={dragLeave}
      onFrameDrop={dragLeave}
      className={cx(
        classes.fileDrop,
        uploadClass,
        draggedFileOver && classes.dropItBackground
      )}
    >
      {!draggedFileOver && (
        <>
          <SvgUpload
            alt={t('resumeUploadButton.imgAlt')}
            className={classes.uploadIcon}
          />
          <Typography className={classes.typography}>
            {t('resumeUploadButton.drag')}
          </Typography>
          <Button
            component="label"
            {...ButtonProps}
            onClick={(e: any) => {
              if (ButtonProps.onClick) {
                ButtonProps.onClick(e)
              }
              handleMpCTAClickTracking(
                e.currentTarget.innerText,
                MixpanelCTALinkActions.ResumeUploadAction,
                mixpanelTracking
              )
            }}
          >
            {children}
            <input
              type="file"
              hidden
              accept={allowedUploadExtensions.join(', ')}
              data-qa-file-upload-input
              // @ts-ignore
              onChange={onFileSelect}
              // What's going on here? Let's say you choose your resume using
              // the file selector and the modal pops up for you to enter your
              // email. But instead of doing that, you accidently close the
              // modal. Then you click the button again to re-upload your
              // resume. In this instance, the onChange event handler won't fire
              // because the file is the same as the one previous. The best way
              // to handle that is to null out the value of the input every time
              // it's clicked.
              onClick={(event) => {
                event.stopPropagation()
                event.currentTarget.value = ''
              }}
            />
          </Button>
          {error && (
            <Trans
              i18nKey="resumeUploadButton.incorrectFileType"
              values={{
                extensions: allowedUploadExtensions
                  .map((ext) => ext.substring(1))
                  .join(', '),
              }}
              parent={ErrorLine}
              components={{ em: <em /> }}
            />
          )}
        </>
      )}
      {draggedFileOver && (
        <>
          <Typography
            variant="h1"
            component="p"
            className={cx(classes.typography, classes.dropIt)}
          >
            {t('resumeUploadButton.drop')}
          </Typography>
          <Typography className={classes.typography}>
            {t('resumeUploadButton.dropDescription')}
          </Typography>
        </>
      )}
    </FileDrop>
  )
}

export const ConnectedResumeUploadButton: React.FC<Omit<Props, 'error'>> = ({
  onFileSelect,
  ...props
}) => {
  const { errors, setResumeFile } = useLeadActions()

  const handleFileSelect = useCallback<ResumeUploadButtonCallback>(
    (event: ResumeUploadButtonEventType) => {
      let file: File | null = null

      if ('files' in event.target && event.target.files?.[0]) {
        file = event.target.files[0]
      } else if ('dataTransfer' in event && event.dataTransfer.files?.[0]) {
        file = event.dataTransfer.files[0]
        // On MacOS (at least) dropping a file like this without preventDefault
        // will cause the document to open in a new tab.
        event.preventDefault()
      }

      if (!file) {
        return
      }

      if (setResumeFile(file)) {
        // Ignoring this because the types are right but a union between drag
        // and change event isn't discernable enough for Typescript to accept
        // it, for whatever reason.
        // @ts-ignore
        onFileSelect(event)
      }
    },
    [setResumeFile, onFileSelect]
  )

  return (
    <ResumeUploadButton
      error={errors.resumeFile}
      onFileSelect={handleFileSelect}
      {...props}
    />
  )
}

const ErrorLine = withStyles(Typography, (theme) => ({
  root: {
    color: theme.palette.secondary.light,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '.9em',
    width: '80%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: '.5em',

    '& em': {
      fontWeight: 'normal',
    },
  },
}))

const useStyles = makeStyles()((theme) => ({
  button: {
    width: '100%',
  },
  fileDrop: {
    backgroundColor:
      theme.palette.primary.contrastText ?? theme.palette.primary.dark,
  },
  dropItBackground: {
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  typography: {
    fontSize: '1em',
    margin: '1.08em 0 1.66em',
  },
  dropIt: {
    fontSize: '1.5em',
    marginBottom: '1em',
  },
  uploadIcon: {
    color: theme.palette.primary.main,
    maxWidth: '5rem',
  },
}))

export default ResumeUploadButton
